// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import buttonSizeVariants from "@insite/mobius/Button/buttonSizeVariants";
import buttonTypes from "@insite/mobius/Button/buttonTypes";
import ButtonPresentationPropsDefault from "@insite/mobius/Button/presentationProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import { Link } from "@insite/mobius/index";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyImage from "@store/MobiusOverrides/LazyImage/LazyImage";
import { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import constants from "@store/storeConstants";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import parse from "@toolkit/Framework/Common/Utilities/parseFullPageHtml";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { buttonThemeStyles } from "@toolkit/Themes/common/mixins";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    currentLanguage: state.context.session.language,
});

type Props = WidgetProps & HasCategoryContext & ReturnType<typeof mapStateToProps>;

export interface ProductListHeadingContentStyles {
    wrapper?: InjectableCss;
    container?: GridContainerProps;
    imageGridItem?: GridItemProps;
    subContainerGridItem?: GridItemProps;
    subContainer?: GridContainerProps;
    textGridItem?: GridItemProps;
    linkGridItem?: GridItemProps;
    buttonLink: LinkPresentationProps;
}

export const productListHeadingContentStyles: ProductListHeadingContentStyles = {
    container: {
        css: css`
            padding: ${pxToRem(22)};
            background-color: ${getColor("common.background")};
            margin: ${pxToRem(20)} 0;
        `,
    },
    imageGridItem: {
        width: [12, 3, 3, 2, 2],
        css: css`
            justify-content: center;
            align-items: center;
        `,
    },
    subContainerGridItem: {
        css: css`
            align-items: center;
        `,
    },
    subContainer: {
        gap: 16,
    },
    textGridItem: {
        css: css`
            align-items: center;
        `,
    },
    linkGridItem: {
        width: [12, 12, 12, 3, 3],
        css: css`
            ${breakpointValues(["center", "left", "left", "center", "center"], "justify-content")}
            align-items: center;
        `,
    },
    buttonLink: {
        typographyProps: {
            size: buttonSizeVariants.small.fontSize,
            weight: ButtonPresentationPropsDefault?.typographyProps?.weight,
        },
        css: css`
            ${({ theme }) => buttonTypes.outline({ _color: "primary", hoverStyle: {}, theme })}
            ${buttonThemeStyles("tertiary")}
            max-width: 100%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            padding: 0rem 1rem;
            text-transform: uppercase;
            min-height: ${pxToRem(40)};
            border-radius: 0.25rem;
            ${transitionHoverStyles("color", "background-color", "border-color")};
        `,
    },
};

const styles = productListHeadingContentStyles;

const ProductListHeadingContent = ({ category, currentLanguage }: Props) => {
    if (!category || !currentLanguage) {
        return null;
    }
    const {
        contentWidgetPLPImage: contentWidgetPLPImage_en,
        contentWidgetPLPText: contentWidgetPLPText_en,
        contentWidgetPLPUrl: contentWidgetPLPUrl_en,
        contentWidgetPLPButtonLabel: contentWidgetPLPButtonLabel_en,
        contentWidgetPLPImage_fr,
        contentWidgetPLPText_fr,
        contentWidgetPLPUrl_fr,
        contentWidgetPLPButtonLabel_fr,
    } = category.properties;

    const isFr = currentLanguage?.languageCode === constants.languageCodes.FR_CA;
    const contentWidgetPLPImage = isFr ? contentWidgetPLPImage_fr : contentWidgetPLPImage_en;
    const contentWidgetPLPText = isFr ? contentWidgetPLPText_fr : contentWidgetPLPText_en;
    const contentWidgetPLPUrl = isFr ? contentWidgetPLPUrl_fr : contentWidgetPLPUrl_en;
    const contentWidgetPLPButtonLabel = isFr ? contentWidgetPLPButtonLabel_fr : contentWidgetPLPButtonLabel_en;

    if (!contentWidgetPLPImage && !contentWidgetPLPText && !contentWidgetPLPUrl) {
        return null;
    }

    const subContainerContentWidth: GridWidths[] | GridWidths = contentWidgetPLPImage ? [12, 9, 9, 10, 10] : 12;
    const textContentWidth: GridWidths[] | GridWidths =
        contentWidgetPLPUrl && contentWidgetPLPButtonLabel ? [12, 12, 12, 9, 9] : 12;

    return (
        <StyledWrapper {...styles.wrapper}>
            <GridContainer {...styles.container}>
                {contentWidgetPLPImage && (
                    <GridItem {...styles.imageGridItem}>
                        <LazyImage src={contentWidgetPLPImage} />
                    </GridItem>
                )}
                <GridItem width={subContainerContentWidth} {...styles.subContainerGridItem}>
                    <GridContainer {...styles.subContainer}>
                        {contentWidgetPLPText && (
                            <GridItem width={textContentWidth} {...styles.textGridItem}>
                                {parse(contentWidgetPLPText, parserOptions)}
                            </GridItem>
                        )}
                        {contentWidgetPLPUrl && contentWidgetPLPButtonLabel && (
                            <GridItem {...styles.linkGridItem}>
                                <Link href={contentWidgetPLPUrl} {...styles.buttonLink}>
                                    {contentWidgetPLPButtonLabel}
                                </Link>
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(ProductListHeadingContent)),
    definition: {
        group: "Product List",
        displayName: "Heading Content",
        allowedContexts: ["ProductListPage", "CategoryDetailsPage", "ProductDetailsPage"],
    },
};

export default widgetModule;
